// Rebuy bundles are discounted by an automatic "Rebuy Product Discount" that targets every cart
// line carrying the bundle properties this theme writes at add time (_source: 'Rebuy'). The
// discount is allocated per line, so removing one bundle member leaves the survivors still tagged
// — and therefore still discounted. Rebuy's own docs say breaking a bundle should revert every
// member to its original price, and that this requires a developer when Rebuy Smart Cart is not in
// use (this theme uses a custom Dawn cart drawer).
//
// frequently-bought.js stamps each add with a unique `_rebuy_bundle_id` and the `_rebuy_bundle_size`
// it was added with. This validator watches cart updates, and when a bundle group has fewer lines
// than its recorded size, it strips the Rebuy properties off the survivors (remove + re-add without
// the tag) so they lose the discount and fall back to full price.

const BUNDLE_ID_KEY = '_rebuy_bundle_id';
const BUNDLE_SIZE_KEY = '_rebuy_bundle_size';

// Guards against re-entrancy: our own remove/re-add fires further cart-data-updated events.
let reconciling = false;

function getSectionInnerHTML(html, selector = '.shopify-section') {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? '';
}

// Reuse the theme's native per-item loading spinner (assets/component-loading-overlay.css) — the
// same one a normal line removal shows (assets/cart.js enableLoading/disableLoading) — while the
// bundle reconcile is in flight, so the shopper never sees the in-between prices.
function setCartLoading(isLoading) {
  document
    .querySelectorAll('cart-drawer .loading-overlay, #main-cart-items .loading-overlay')
    .forEach((el) => el.classList.toggle('hidden', !isLoading));

  ['#CartDrawer-CartItems', '#main-cart-items'].forEach((sel) => {
    document.querySelector(sel)?.classList.toggle('cart__items--disabled', isLoading);
  });
}

// Group cart lines by their bundle id. Lines without a bundle id are ignored.
function groupBundles(cart) {
  const groups = new Map();
  (cart?.items || []).forEach((item) => {
    const bundleId = item.properties?.[BUNDLE_ID_KEY];
    if (!bundleId) return;
    const group = groups.get(bundleId) || [];
    groups.set(bundleId, [...group, item]);
  });
  return groups;
}

// A bundle is broken when at least one — but not all — of its members has been removed.
function findBrokenSurvivors(cart) {
  const survivors = [];
  groupBundles(cart).forEach((lines) => {
    const expected = parseInt(lines[0]?.properties?.[BUNDLE_SIZE_KEY], 10);
    if (Number.isFinite(expected) && lines.length > 0 && lines.length < expected) {
      survivors.push(...lines);
    }
  });
  return survivors;
}

const CART_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

// Paint the cart drawer from a cart state that already carries rendered `sections`, and broadcast it
// so other listeners (widget CTA, free-gift validator) resync. `protocol-cart-summary` owns its own
// render, so defer to it when present.
function applyCartState(state) {
  const protocolCartSummary = document.querySelector('protocol-cart-summary');
  if (protocolCartSummary && typeof protocolCartSummary.refresh === 'function') {
    protocolCartSummary.refresh();
    return;
  }

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function' && state?.sections) {
    cartDrawer.getSectionsToRender().forEach((section) => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!el || !state.sections[section.id]) return;
      el.innerHTML = getSectionInnerHTML(state.sections[section.id], section.selector);
    });
    cartDrawer.classList.toggle('is-empty', !state.item_count);
  }

  document.dispatchEvent(
    new CustomEvent('cart-data-updated', { bubbles: true, detail: { cartData: state } })
  );
}

// Fallback resync (used only if the mutation response lacked rendered sections): fetch the drawer
// sections and the cart in parallel, then apply.
async function fetchCartState() {
  const cartDrawer = document.querySelector('cart-drawer');
  const sectionIds =
    cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map((s) => s.id).join(',')
      : '';
  const [sections, cart] = await Promise.all([
    sectionIds
      ? fetch(`${window.location.pathname}?sections=${sectionIds}`, { headers: CART_HEADERS }).then((r) => r.json())
      : Promise.resolve({}),
    fetch('/cart.js').then((r) => r.json()),
  ]);
  return { ...cart, sections };
}

// Strip the Rebuy tag from the broken bundle's survivors so the discount no longer applies.
// Properties can't be edited in place, so each survivor is re-added without the tag and the tagged
// line removed. Order matters: re-add FIRST, then remove the tagged lines by their line-item key
// (precise — never a standalone copy of the same variant). Re-adding first means the cart is never
// momentarily empty and survivors are never lost if a request fails. The remove call requests the
// rendered drawer sections back, so the re-render needs no extra round-trips.
async function revertSurvivors(survivors) {
  reconciling = true;
  setCartLoading(true);

  const root = window.Shopify?.routes?.root || '/';
  const cartDrawer = document.querySelector('cart-drawer');
  const sectionIds =
    cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
      ? cartDrawer.getSectionsToRender().map((s) => s.id).join(',')
      : '';

  try {
    const items = survivors.map((line) => ({ id: line.variant_id ?? line.id, quantity: line.quantity }));
    await fetch(`${root}cart/add.js`, { method: 'POST', headers: CART_HEADERS, body: JSON.stringify({ items }) });

    const updates = survivors.reduce((acc, line) => ({ ...acc, [line.key]: 0 }), {});
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: CART_HEADERS,
      body: JSON.stringify({ updates, sections: sectionIds, sections_url: window.location.pathname }),
    });
    const state = await response.json();

    applyCartState(state.sections ? state : await fetchCartState());
  } catch (err) {
    console.warn('[RebuyBundleValidator] Failed to revert broken bundle:', err);
    await fetchCartState().then(applyCartState).catch(() => {});
  } finally {
    reconciling = false;
    setCartLoading(false);
  }
}

export function initRebuyBundleValidator() {
  document.addEventListener('cart-data-updated', (event) => {
    if (reconciling) return;
    const cart = event.detail?.cartData;
    if (!cart) return;
    const survivors = findBrokenSurvivors(cart);
    if (survivors.length > 0) revertSurvivors(survivors);
  });
}
